import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Avatar,
  Stack,
  Divider,
  OutlinedInput,
  Grid,
  IconButton,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  useGetQuestionQuery,
  useAddCommentMutation,
  useEditQuestionMutation,
  useGetSchoolQuestionsQuery,
  useGetPendingReviewQuestionsQuery,
} from "../../../../../../app/questionApiSlice";
import toast from "../../../../../../helpers/toast";
import { selectCurrentUser } from "../../../../auth/authSlice";
import { stripHtmlTags } from "../../../../../../helpers/htmlToText";
import MathContent from "../../../../../customComponents/MathContent";
import CustomWarningDialog from "../../../../../customComponents/CustomWarningDialog";
import { useFormik } from "formik";
import * as yup from "yup";

export default function ReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const currentUser = useSelector(selectCurrentUser);
  const listContext = location.state?.listContext;
  const isTeacherReviewsRoute = location.pathname.startsWith("/lms/reviews/");
  const itemsPerPage = listContext?.perPage || 10;
  const [listPage, setListPage] = useState(listContext?.page || 1);

  const { data, isLoading, isFetching, error } = useGetQuestionQuery(id);
  const [addComment, { isLoading: isCommenting }] = useAddCommentMutation();
  const [approveQuestion, { isLoading: isApproving }] = useEditQuestionMutation();

  // Fetch the pending review list for prev/next navigation
  const listQueryArgs = {
    page: listPage,
    per_page: itemsPerPage,
    sort_by: listContext?.sortBy || "date",
  };

  const { data: teacherListData } = useGetSchoolQuestionsQuery(
    {
      ...listQueryArgs,
      subject:
        listContext?.filterSubject && listContext.filterSubject !== "all"
          ? listContext.filterSubject
          : null,
      level:
        listContext?.filterLevel && listContext.filterLevel !== "all"
          ? listContext.filterLevel
          : null,
    },
    { skip: !isTeacherReviewsRoute }
  );

  const { data: reviewerListData } = useGetPendingReviewQuestionsQuery(listQueryArgs, {
    skip: isTeacherReviewsRoute,
  });

  const listData = isTeacherReviewsRoute ? teacherListData : reviewerListData;

  const listQuestions = listData?.questions || [];
  const totalQuestions = listData?.pagination?.total || 0;
  const totalPages = listData?.pagination?.pages || 0;

  // Find current question index in the list
  const currentIndex = useMemo(
    () => listQuestions.findIndex((q) => String(q.id) === String(id)),
    [listQuestions, id]
  );

  // Global position: (listPage - 1) * itemsPerPage + currentIndex
  const globalIndex = currentIndex >= 0 ? (listPage - 1) * itemsPerPage + currentIndex : -1;
  const hasPrev = globalIndex > 0;
  const hasNext = globalIndex >= 0 && globalIndex < totalQuestions - 1;
  const [pendingNav, setPendingNav] = useState(null);
  const [isNavLoading, setIsNavLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  useEffect(() => {
    setIsNavLoading(false);
  }, [id]);

  // If current question isn't on this page, try to find which page it's on
  useEffect(() => {
    if (!pendingNav && listQuestions.length > 0 && currentIndex === -1 && listPage < totalPages) {
      // Question not found on current page — search next page
      setListPage((p) => Math.min(p + 1, totalPages));
    }
  }, [currentIndex, listPage, listQuestions, pendingNav, totalPages]);

  const handlePrev = () => {
    if (currentIndex > 0) {
      // Previous question is on the same page
      setIsNavLoading(true);
      navigate(`${isTeacherReviewsRoute ? "/lms/reviews" : "/lms/questions/review"}/${listQuestions[currentIndex - 1].id}`, {
        state: {
          listContext: {
            ...listContext,
            source: isTeacherReviewsRoute ? "school-reviews" : "pending-review",
            page: listPage,
            perPage: itemsPerPage,
            sortBy: listContext?.sortBy || "date",
          },
        },
      });
    } else if (listPage > 1) {
      // Need to go to previous page — navigate will trigger re-fetch
      setIsNavLoading(true);
      setListPage(listPage - 1);
      // We'll navigate once that page loads, using a flag
      setPendingNav("prev");
    }
  };

  const handleNext = () => {
    if (currentIndex < listQuestions.length - 1) {
      // Next question is on the same page
      setIsNavLoading(true);
      navigate(`${isTeacherReviewsRoute ? "/lms/reviews" : "/lms/questions/review"}/${listQuestions[currentIndex + 1].id}`, {
        state: {
          listContext: {
            ...listContext,
            source: isTeacherReviewsRoute ? "school-reviews" : "pending-review",
            page: listPage,
            perPage: itemsPerPage,
            sortBy: listContext?.sortBy || "date",
          },
        },
      });
    } else if (listPage < totalPages) {
      // Need to go to next page
      setIsNavLoading(true);
      setListPage(listPage + 1);
      setPendingNav("next");
    }
  };

  // Handle cross-page navigation
  useEffect(() => {
    if (pendingNav && listQuestions.length > 0 && currentIndex === -1) {
      const nextQuestionId =
        pendingNav === "next"
          ? listQuestions[0].id
          : listQuestions[listQuestions.length - 1].id;

      if (pendingNav === "next") {
        navigate(
          `${isTeacherReviewsRoute ? "/lms/reviews" : "/lms/questions/review"}/${nextQuestionId}`,
          {
            state: {
              listContext: {
                ...listContext,
                source: isTeacherReviewsRoute ? "school-reviews" : "pending-review",
                page: listPage,
                perPage: itemsPerPage,
                sortBy: listContext?.sortBy || "date",
              },
            },
          }
        );
      } else if (pendingNav === "prev") {
        navigate(
          `${isTeacherReviewsRoute ? "/lms/reviews" : "/lms/questions/review"}/${nextQuestionId}`,
          {
            state: {
              listContext: {
                ...listContext,
                source: isTeacherReviewsRoute ? "school-reviews" : "pending-review",
                page: listPage,
                perPage: itemsPerPage,
                sortBy: listContext?.sortBy || "date",
              },
            },
          }
        );
      }
      setPendingNav(null);
    }
  }, [
    currentIndex,
    isTeacherReviewsRoute,
    itemsPerPage,
    listContext,
    listPage,
    listQuestions,
    navigate,
    pendingNav,
  ]);

  const question = data?.question || {};
  const reviews = question.reviews || [];

  // Check if current user is the creator of this question
  const isOwnQuestion = question.user_id === currentUser?.id;

  const commentFormik = useFormik({
    initialValues: { body: "" },
    validationSchema: yup.object({
      body: yup.string().required("Comment cannot be empty"),
    }),
    onSubmit: async (values) => {
      try {
        await addComment({
          body: values.body,
          question_id: id,
        }).unwrap();
        commentFormik.resetForm();
      } catch (err) {
        console.error("Error adding comment:", err);
      }
    },
  });

  const handleRejectConfirm = async () => {
    setRejectDialogOpen(false);
    try {
      await approveQuestion({
        values: { is_approved: false, approver_id: null },
        questionId: id,
      }).unwrap();
      toast.success("Question rejected");
    } catch (err) {
      console.error("Error rejecting question:", err);
      const msg = err?.data?.message || err?.message || "Failed to reject question.";
      toast.error(msg);
    }
  };

  if (isLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );

  if (error || !question.id || isOwnQuestion)
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: "primary.safeOnLight" }}
          >
            Back
          </Button>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error" variant="h6">
            {isOwnQuestion
              ? "You cannot review your own questions."
              : "Question not found or an error occurred."}
          </Typography>
        </Box>
      </Container>
    );

  const handleBackClick = () => {
    navigate(isTeacherReviewsRoute ? "/lms/reviews" : "/lms/questions/review");
  };

  const showNavLoader = isNavLoading || pendingNav !== null || isFetching;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        {/* Top navigation bar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
          >
            Back to Reviews
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {globalIndex >= 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary", mr: 1 }}>
                {globalIndex + 1} of {totalQuestions}
              </Typography>
            )}
            {showNavLoader && <CircularProgress size={16} thickness={5} />}
            <IconButton
              onClick={handlePrev}
              disabled={!hasPrev || showNavLoader}
              size="small"
              sx={{ border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#e0e0e0"}` }}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              disabled={!hasNext || showNavLoader}
              size="small"
              sx={{ border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#e0e0e0"}` }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </Box>

        <Paper
          sx={{
            py: 4,
            px: { xs: 2, md: 8 },
            bgcolor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
            borderRadius: "8px",
            position: "relative",
            minHeight: 200,
          }}
        >
          {isFetching && !isLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "rgba(245,245,245,0.7)",
                borderRadius: "8px",
                zIndex: 1,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
          {/* Subject & Level */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            {question.subject && (
              <Typography variant="body2" sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? "#1a2a3a" : "#E8F0FE", color: "#2371d1", px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 500 }}>
                {question.subject}
              </Typography>
            )}
            {question.level && (
              <Typography variant="body2" sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? "#2d1a4a" : "#F3E8FF", color: "#7C3AED", px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 500 }}>
                {question.level}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: { xs: "block", md: "flex" },
              alignItems: "flex-start",
              gap: 5,
            }}
          >
            <Box
              sx={{
                bgcolor: "primary.main",
                color: "white",
                width: "fit-content",
                px: "20px",
                py: "15px",
                borderRadius: "72px",
                fontWeight: 700,
                fontSize: "20px",
              }}
            >
              {question.id}
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1 }}>
              <Grid container rowSpacing={3} columnSpacing={4}>
                <Grid item xs={12}>
                  <Typography sx={{ p: 1, fontSize: "16px", fontWeight: 500 }}>
                    <MathContent html={question.body} />
                  </Typography>
                </Grid>
                {question.options?.map((option) => (
                  <Grid key={option?.id} item xs={12} md={6}>
                    <Box
                      sx={{
                        border: "2px solid",
                        borderColor: option?.is_answer ? "#27AE60" : "#E02424",
                        borderRadius: "14px",
                        padding: "17px",
                        fontWeight: 500,
                        color: option?.is_answer ? "#27AE60" : theme.palette.text.primary,
                        backgroundColor: option?.is_answer
                          ? isDark ? "#1b3a27" : "#f0fdf4"
                          : isDark ? "#2e1a1a" : "#fef2f2",
                      }}
                    >
                      <MathContent html={option?.body} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
          {question.explanation && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <strong>Explanation:</strong>{" "}
                <MathContent html={question.explanation} />
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Approve button */}
        {!question.is_approved && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={async () => {
                try {
                  await approveQuestion({
                    values: { is_approved: true, approver_id: currentUser?.id },
                    questionId: id,
                  }).unwrap();
                  toast.success("Question approved");
                } catch (err) {
                  console.error("Error approving question:", err);
                  const msg = err?.data?.message || err?.message || "Failed to approve question.";
                  toast.error(msg);
                }
              }}
              disabled={isApproving}
              sx={{ minWidth: 140 }}
            >
              {isApproving ? "Approving..." : "Approve"}
            </Button>
          </Box>
        )}
        {question.is_approved && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ color: "success.main", fontWeight: 600 }}>
              ✓ Approved
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setRejectDialogOpen(true)}
              disabled={isApproving}
              sx={{ minWidth: 100 }}
            >
              {isApproving ? "Processing..." : "Reject"}
            </Button>
          </Box>
        )}

        <Divider />

        {/* Comments */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Comments ({reviews.filter((r) => !r.is_deleted).length})
          </Typography>

          <Stack spacing={0}>
            {reviews.filter((r) => !r.is_deleted).length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary", py: 2 }}>
                No comments yet. Add your comment.
              </Typography>
            ) : (
              reviews
                .filter((r) => !r.is_deleted)
                .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                .map((review) => {
                  const isCurrentUser = review.user_id === currentUser?.id;
                  const isQuestionCreator = review.user_id === question.user_id;
                  const displayName = isCurrentUser ? "You" : isQuestionCreator ? "Teacher" : "Reviewer";
                  const avatarLetter = isCurrentUser ? "Y" : isQuestionCreator ? "T" : "R";
                  const avatarColor = isCurrentUser ? "#2371d1" : isQuestionCreator ? "#27AE60" : "#9e9e9e";
                  return (
                    <Box
                      key={review.id}
                      sx={{
                        display: "flex",
                        gap: 2,
                        py: 2,
                        borderBottom: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#333" : "#f0f0f0"}`,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: 14,
                          bgcolor: avatarColor,
                          flexShrink: 0,
                        }}
                      >
                        {avatarLetter}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {displayName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "gray" }}>
                            {new Date(review.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, wordBreak: "break-word", color: "text.primary" }}
                        >
                          {stripHtmlTags(review.body || "")}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
            )}
          </Stack>

          {/* Comment input */}
          <Box
            component="form"
            onSubmit={commentFormik.handleSubmit}
            sx={{ mt: 3, display: "flex", gap: 2, alignItems: "flex-start" }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                bgcolor: "#2371d1",
                flexShrink: 0,
                mt: 0.5,
              }}
            >
              You
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <OutlinedInput
                placeholder="Add a comment..."
                fullWidth
                multiline
                minRows={2}
                value={commentFormik.values.body}
                onChange={commentFormik.handleChange}
                name="body"
                sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff" }}
              />
              {commentFormik.errors.body && commentFormik.touched.body && (
                <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5 }}>
                  {commentFormik.errors.body}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isCommenting || !commentFormik.values.body.trim()}
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  {isCommenting ? "Sending..." : "Comment"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

      </Stack>
      <CustomWarningDialog
        open={rejectDialogOpen}
        setOpen={setRejectDialogOpen}
        message={"Are you sure you want to reject this approved question?"}
        action={() => handleRejectConfirm()}
        item={id}
      />
    </Container>
  );
}
